/**
 * @file use-vat-form-state.ts
 *
 * Controlled-form state for the VAT & taxes form, mirroring
 * `use-settings-form-state.ts`. Holds the editable fields, exposes a `patch`
 * updater, and validates the standard rate (required only while VAT is active).
 * Errors surface only after the first `validate()`. Re-seeds when the seed
 * reference changes (the screen passes a fresh seed once the settings load).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import type { VatFormErrors, VatFormFields } from './vat-form-types';

/** What {@link useVatFormState} returns to the form. */
export interface UseVatFormStateResult {
  fields: VatFormFields;
  patch: (update: Partial<VatFormFields>) => void;
  errors: VatFormErrors;
  validate: () => boolean;
  /** Attach to the rate field; focused/scrolled to when validation fails on it. */
  firstErrorRef: RefObject<HTMLInputElement | null>;
}

/**
 * Manages the VAT & taxes form's field state and validation.
 *
 * @param seed - The initial fields, derived from the loaded VAT settings;
 *   re-seeds the form when its reference changes.
 *
 * @returns The fields, updater, errors, and a `validate` function.
 */
export function useVatFormState(seed: VatFormFields): UseVatFormStateResult {
  const intl = useIntl();
  const [fields, setFields] = useState<VatFormFields>(seed);
  const seededRef = useRef(seed);
  const [submitted, setSubmitted] = useState(false);
  const firstErrorRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (seededRef.current === seed) {
      return;
    }
    seededRef.current = seed;
    setFields(seed);
    setSubmitted(false);
  }, [seed]);

  const patch = useCallback((update: Partial<VatFormFields>) => {
    setFields((prev) => ({ ...prev, ...update }));
  }, []);

  const computeErrors = useCallback(
    (current: VatFormFields): VatFormErrors => {
      const errors: VatFormErrors = {};
      const trimmed = current.standardRate.trim();
      if (current.enabled && trimmed === '') {
        errors.standardRateError = intl.formatMessage(adminMessages.paymentsVatRateRequired);
      } else if (trimmed !== '') {
        // The backend stores the rate as a number in [0, 100] and rejects enabling VAT
        // without a positive rate. Validate here so a non-numeric or out-of-range entry
        // (e.g. a comma decimal like "12,5", which `Number()` turns into `NaN` → `null`
        // on the wire) surfaces a clear message instead of a generic 422 on save.
        const rate = Number(trimmed);
        const invalid =
          !Number.isFinite(rate) || rate < 0 || rate > 100 || (current.enabled && rate <= 0);
        if (invalid) {
          errors.standardRateError = intl.formatMessage(adminMessages.paymentsVatRateInvalid);
        }
      }
      return errors;
    },
    [intl],
  );

  const errors = useMemo(
    () => (submitted ? computeErrors(fields) : {}),
    [submitted, computeErrors, fields],
  );

  const validate = useCallback((): boolean => {
    setSubmitted(true);
    const result = computeErrors(fields);
    const valid = Object.keys(result).length === 0;
    if (!valid && result.standardRateError) {
      firstErrorRef.current?.focus?.();
      firstErrorRef.current?.scrollIntoView?.({ block: 'center' });
    }
    return valid;
  }, [computeErrors, fields]);

  return { fields, patch, errors, validate, firstErrorRef };
}
